/** @file Reads the rig direction set: layout default, iso tile size and per-direction fix data. */
import type { Dir, Pose2DJson } from '@pose2d/format/types';
import type { CompiledDrawOrderOp, DirectionEntryData, DirectionSetData } from '../../skeletonData';
import { coreFail } from '../coreFail';
import { DIRECTION_TABLE } from '../direction/directionTable';
import { indexIn } from './indexIn';

type RigDirectionEntry = NonNullable<NonNullable<Pose2DJson['directions']>['entries'][Dir]>;
type Counts = { slots: number; skins: number; animations: number };

/** One entry; mirror modes always flip (§5.8), an authored entry keeps the rig's flipX (default false). */
function readEntry(e: RigDirectionEntry, path: string, counts: Counts): DirectionEntryData {
  const fixAnimations = new Map<string, number>();
  for (const [base, anim] of Object.entries(e.fix?.animations ?? {})) {
    fixAnimations.set(base, indexIn(anim, counts.animations, `${path}.fix.animations.${base}`));
  }
  const fixDrawOrderOps = (e.fix?.drawOrderOps ?? []).map(([op, a, b], k): CompiledDrawOrderOp => [
    op,
    indexIn(a, counts.slots, `${path}.fix.drawOrderOps[${k}]`),
    indexIn(b, counts.slots, `${path}.fix.drawOrderOps[${k}]`),
  ]);
  return {
    mode: e.mode,
    source: e.source ?? null,
    flipX: e.mode === 'authored' ? (e.flipX ?? false) : true,
    fixSkin: e.fix?.skin === undefined ? -1 : indexIn(e.fix.skin, counts.skins, `${path}.fix.skin`),
    fixDrawOrderOps,
    fixAnimations,
  };
}

/**
 * The iso tile size, or null when the rig has none. Both sides must be finite and positive: a zero, negative or
 * NaN side makes the diagonal sector skew (atan2(h, w)) degenerate, so `resolveDirection` could not be trusted.
 */
function readIso(iso: [number, number] | undefined): [number, number] | null {
  if (iso === undefined) return null;
  const [w, h] = iso;
  const valid = Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0;
  if (!valid) {
    coreFail('VALIDATION', `iso tile size must be two finite positive numbers, got [${String(w)}, ${String(h)}]`, {
      path: 'directions.iso',
      hint: 'iso is [tileW, tileH] in pixels, for example [64, 37]',
    });
  }
  return [w, h];
}

/** The set (entries in DIRECTION_TABLE.all order; unknown keys ignored), or null when the rig has none. */
export function readDirections(set: Pose2DJson['directions'], counts: Counts): DirectionSetData | null {
  if (set === undefined) return null;
  const entries = new Map<Dir, DirectionEntryData>();
  for (const dir of DIRECTION_TABLE.all) {
    const entry = set.entries[dir];
    if (entry !== undefined) entries.set(dir, readEntry(entry, `directions.entries.${dir}`, counts));
  }
  return {
    count: set.count,
    layout4: set.layout4 ?? 'diagonal',
    iso: readIso(set.iso),
    entries,
  };
}
